/** Save a Blob to disk under `filename` via a transient object URL. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Trigger a client-side download of a JSON-serializable value. */
export function downloadJson(filename: string, value: unknown): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  downloadBlob(filename.endsWith('.json') ? filename : `${filename}.json`, blob);
}

/** Trigger a client-side download of plain text. */
export function downloadText(filename: string, text: string): void {
  downloadBlob(filename, new Blob([text], { type: 'text/plain' }));
}

/**
 * Copy text to the clipboard, returning whether it succeeded.
 * Prefers the async Clipboard API; falls back to a hidden textarea +
 * execCommand for insecure (non-HTTPS) contexts where it's unavailable.
 */
export async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or non-secure context — fall through to legacy path.
    }
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Zip `files` (name → text or bytes) at the archive root and download it. */
export async function downloadZip(filename: string, files: Record<string, string | Uint8Array>): Promise<void> {
  // Loaded on demand: only this button needs it, so keep it out of the main bundle.
  const { zipSync, strToU8 } = await import('fflate');
  const entries: Record<string, Uint8Array> = {};
  for (const [name, body] of Object.entries(files)) entries[name] = typeof body === 'string' ? strToU8(body) : body;
  const zipped = zipSync(entries, { level: 6 });
  downloadBlob(filename.endsWith('.zip') ? filename : `${filename}.zip`, new Blob([zipped], { type: 'application/zip' }));
}
